import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { IconBase } from './icon-base';
import type { InputSignal, Signal, WritableSignal } from '@angular/core';

@Component({
  selector: 'td-wb-icon',
  template: `
    <svg focusable="false"
         role="img"
         [attr.aria-hidden]="decorative() ? 'true' : null"
         [attr.aria-label]="ariaLabel()"
         [attr.aria-labelledby]="ariaLabelledBy()"
         [style]="iconSizeStyle()">
      <use class="td-wb-svg-icon"
           [attr.xlink:href]="iconHref()"></use>
    </svg>`,
  styleUrls: ['icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()'
  }
})
export class IconComponent extends IconBase {
  readonly size: InputSignal<string | undefined> = input<string>();
  readonly iconId: InputSignal<string | undefined> = input<string>();
  readonly iconType: InputSignal<'primary' | 'primary2' | 'primary3' | 'inverse' | 'secondary' | undefined> = input<'primary' | 'primary2' | 'primary3' | 'inverse' | 'secondary'>();
  readonly decorative: InputSignal<boolean> = input<boolean>(true);
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly inline: InputSignal<boolean> = input<boolean>(false);
  readonly className: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'class' });

  private readonly manualSize: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  readonly effectiveSize: Signal<string | undefined> = computed(() => this.manualSize() ?? this.size());

  readonly iconHref: Signal<string> = computed(() => {
    const id = this.iconId()?.trim() ?? '';
    return `#${id}`;
  });

  readonly iconSizeStyle: Signal<{ width: string; height: string } | undefined> = computed(() => {
    const size = this.effectiveSize();
    return size?.trim() ? this.getSizeStyle(size) : undefined;
  });

  readonly hostClasses: Signal<string> = computed(() => {
    const iconId = this.iconId()?.trim() ?? '';
    const result = [this.className(), `td-wb-icon td-wb-icon--${iconId}`];

    if (this.inline()) {
      result.push('td-wb-icon--inline');
    }
    const type = this.iconType();
    if (type?.trim()) {
      result.push(`td-wb-icon--${type}`);
    }
    return result.filter(Boolean).join(' ');
  });

  setIconSize(size: string): void {
    this.manualSize.set(size);
  }
}
